import { useState } from "react";
import { toast } from "sonner";
import { useJobLog } from "@/hooks/use-job-log";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  fmt, conductorAreas, getFillPercent, conduitSizes, BOX_FILL_VOLUMES, STANDARD_BOXES,
  RACEWAY_TYPES, BEND_RADII, BENDER_SHOES, BEND_MATH, TAKEUP_90,
  type WireEntry,
} from "./electrical-helpers";
import { readSettings } from "@/hooks/use-settings";

const defaults = readSettings();
const defaultRacewayIdx = String(Math.max(0, RACEWAY_TYPES.findIndex(r => r.value === defaults.racewayType)));

export const ConduitFillCalc = ({ onSave }: { onSave?: (entry: Parameters<ReturnType<typeof useJobLog>["addEntry"]>[0]) => void }) => {
  const [wireEntries, setWireEntries] = useState<WireEntry[]>([
    { id: crypto.randomUUID(), size: "12", count: 3 },
  ]);

  const addEntry = () =>
    setWireEntries((prev) => [...prev, { id: crypto.randomUUID(), size: "12", count: 1 }]);

  const removeEntry = (id: string) =>
    setWireEntries((prev) => (prev.length > 1 ? prev.filter((e) => e.id !== id) : prev));

  const updateEntry = (id: string, field: keyof Omit<WireEntry, "id">, value: string) =>
    setWireEntries((prev) =>
      prev.map((e) =>
        e.id === id
          ? { ...e, [field]: field === "count" ? Math.max(0, parseInt(value, 10) || 0) : value }
          : e
      )
    );

  const validEntries = wireEntries.filter((e) => {
    const c = conductorAreas.find((ca) => ca.value === e.size);
    return c && e.count > 0;
  });

  const totalWires = validEntries.reduce((sum, e) => sum + e.count, 0);
  const totalArea = validEntries.reduce((sum, e) => {
    const c = conductorAreas.find((ca) => ca.value === e.size)!;
    return sum + c.area * e.count;
  }, 0);

  const fillLimit = getFillPercent(totalWires);
  const isValid = totalWires > 0;

  const minConduit = isValid
    ? conduitSizes.find((c) => c.area * fillLimit >= totalArea)
    : null;

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        NEC Chapter 9 Tables 4 &amp; 5 — Determines minimum conduit size for mixed THHN/THWN-2 conductors in EMT.
      </p>

      {wireEntries.map((entry, idx) => (
        <div key={entry.id} className="flex items-end gap-2">
          <div className="flex-1">
            {idx === 0 && (
              <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Wire Size</label>
            )}
            <Select value={entry.size} onValueChange={(v) => updateEntry(entry.id, "size", v)}>
              <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
              <SelectContent>
                {conductorAreas.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-20">
            {idx === 0 && (
              <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Qty</label>
            )}
            <Input type="number" inputMode="numeric" value={entry.count || ""} onChange={(e) => updateEntry(entry.id, "count", e.target.value)} placeholder="#" className="bg-secondary text-foreground border-border font-mono-data text-lg" />
          </div>
          <button type="button" onClick={() => removeEntry(entry.id)} className="h-10 w-10 flex items-center justify-center rounded-md border border-border text-muted-foreground hover:text-destructive hover:border-destructive/50 transition-colors shrink-0" disabled={wireEntries.length === 1}>✕</button>
        </div>
      ))}

      <button type="button" onClick={addEntry} className="w-full h-9 rounded-md border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors">+ Add Wire Size</button>

      {isValid && (
        <div className="space-y-2 animate-fade-in">
          <div className="trade-card border-muted/40">
            <span className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">Conductor Breakdown</span>
            {validEntries.map((e) => {
              const c = conductorAreas.find((ca) => ca.value === e.size)!;
              return (
                <div key={e.id} className="flex justify-between text-xs font-mono-data">
                  <span className="text-muted-foreground">{e.count}× {c.label}</span>
                  <span className="text-foreground">{fmt(c.area * e.count, 4)} in²</span>
                </div>
              );
            })}
            <div className="flex justify-between text-sm font-mono-data font-bold border-t border-border mt-1 pt-1">
              <span className="text-muted-foreground">{totalWires} total</span>
              <span className="text-foreground">{fmt(totalArea, 4)} in²</span>
            </div>
          </div>

          <div className="trade-card border-accent/40 bg-accent/5">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Fill Limit</span>
              <span className="font-mono-data text-sm font-bold text-foreground">
                {totalWires === 1 ? "53%" : totalWires === 2 ? "31%" : "40%"}
                <span className="text-[10px] text-muted-foreground font-normal ml-1">({totalWires} {totalWires === 1 ? "wire" : "wires"})</span>
              </span>
            </div>
          </div>

          {minConduit ? (
            <div className="trade-card border-primary/40 bg-primary/5">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Minimum Conduit (EMT)</span>
                <span className="font-mono-data text-xl text-primary font-bold">{minConduit.label}</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Allowable fill: {fmt(minConduit.area * fillLimit, 4)} in² — Used: {fmt(totalArea, 4)} in² ({fmt((totalArea / minConduit.area) * 100, 1)}% actual)
              </p>
            </div>
          ) : (
            <div className="trade-card border-destructive/40 bg-destructive/5">
              <p className="text-sm text-destructive font-semibold">⚠️ Exceeds 4" conduit capacity</p>
              <p className="text-xs text-muted-foreground mt-1">Consider multiple conduit runs or larger raceway types.</p>
            </div>
          )}

          {minConduit && onSave && (
            <button type="button" onClick={() => {
              const wireDesc = validEntries.map((e) => { const c = conductorAreas.find((ca) => ca.value === e.size)!; return `${e.count}× ${c.label}`; }).join(", ");
              onSave({
                calcType: "conduit-fill", label: `${minConduit.label} EMT — ${wireDesc}`,
                inputs: Object.fromEntries(validEntries.map((e, i) => [`wire_${i + 1}`, `${e.count}× ${conductorAreas.find((ca) => ca.value === e.size)!.label}`])),
                results: [
                  { label: "Min Conduit", value: minConduit.label, unit: "EMT" },
                  { label: "Total Area", value: fmt(totalArea, 4), unit: "in²" },
                  { label: "Fill", value: fmt((totalArea / minConduit.area) * 100, 1), unit: "%" },
                  { label: "Total Wires", value: String(totalWires), unit: "" },
                ],
                note: "",
              });
              toast.success("Saved to Job Log");
            }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
          )}

          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-2">All Conduit Sizes</h3>
          <div className="-mx-4 overflow-x-auto">
            <div className="mx-4 rounded-lg border border-border">
              <table className="w-full text-left">
                <thead>
                  <tr>
                    <th className="data-table-header">Size</th>
                    <th className="data-table-header">Area (in²)</th>
                    <th className="data-table-header">Fill Limit</th>
                    <th className="data-table-header">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {conduitSizes.map((c, i) => {
                    const allowable = c.area * fillLimit;
                    const fits = allowable >= totalArea;
                    const isMin = c === minConduit;
                    return (
                      <tr key={i} className={isMin ? "bg-primary/15 ring-1 ring-inset ring-primary/30" : i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                        <td className={`data-table-cell ${isMin ? "text-primary font-bold" : ""}`}>{c.label}</td>
                        <td className="data-table-cell font-mono-data">{c.area}</td>
                        <td className="data-table-cell font-mono-data">{fmt(allowable, 4)}</td>
                        <td className={`data-table-cell font-semibold ${fits ? "text-primary" : "text-destructive"}`}>
                          {fits ? (isMin ? "✓ Min" : "✓") : "✗"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">
        ⚠️ Based on NEC Chapter 9 Tables 4 (EMT) &amp; 5 (THHN/THWN-2). For other raceway or insulation types, consult the applicable NEC tables.
      </p>
    </div>
  );
};

export const BoxFillCalc = ({ onSave }: { onSave?: (entry: Parameters<ReturnType<typeof useJobLog>["addEntry"]>[0]) => void }) => {
  const [wireSize, setWireSize] = useState("14");
  const [conductorCount, setConductorCount] = useState("4");
  const [groundCount, setGroundCount] = useState("1");
  const [clampCount, setClampCount] = useState("1");
  const [deviceCount, setDeviceCount] = useState("1");

  const volPerConductor = BOX_FILL_VOLUMES[wireSize] ?? 2.0;
  const numConductors = Math.max(0, parseInt(conductorCount) || 0);
  const numGrounds = Math.max(0, parseInt(groundCount) || 0);
  const numClamps = Math.max(0, parseInt(clampCount) || 0);
  const numDevices = Math.max(0, parseInt(deviceCount) || 0);

  const conductorVol = numConductors * volPerConductor;
  const clampVol = numClamps > 0 ? 1 * volPerConductor : 0;
  const deviceVol = numDevices * 2 * volPerConductor;
  const groundVol = numGrounds > 0 ? 1 * volPerConductor : 0;
  const totalVol = conductorVol + clampVol + deviceVol + groundVol;

  const hasInput = numConductors > 0;
  const minBox = hasInput ? STANDARD_BOXES.find((b) => b.volume >= totalVol) : null;

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        NEC 314.16(B) — Calculate minimum box volume based on conductors, clamps, devices, and equipment grounds.
      </p>

      <div>
        <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Largest Wire Size in Box</label>
        <Select value={wireSize} onValueChange={setWireSize}>
          <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(BOX_FILL_VOLUMES).map(([size, vol]) => (
              <SelectItem key={size} value={size}>{size} AWG ({vol} in³)</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Conductors</label>
          <Input type="number" inputMode="numeric" value={conductorCount} onChange={(e) => setConductorCount(e.target.value)} placeholder="# of wires" className="bg-secondary border-border font-mono-data text-lg" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Grounds (EGC)</label>
          <Input type="number" inputMode="numeric" value={groundCount} onChange={(e) => setGroundCount(e.target.value)} placeholder="# grounds" className="bg-secondary border-border font-mono-data text-lg" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Cable Clamps</label>
          <Input type="number" inputMode="numeric" value={clampCount} onChange={(e) => setClampCount(e.target.value)} placeholder="# clamps" className="bg-secondary border-border font-mono-data text-lg" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Devices / Yokes</label>
          <Input type="number" inputMode="numeric" value={deviceCount} onChange={(e) => setDeviceCount(e.target.value)} placeholder="# devices" className="bg-secondary border-border font-mono-data text-lg" />
        </div>
      </div>

      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <div className="trade-card border-muted/40">
            <span className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">Fill Breakdown</span>
            <div className="space-y-0.5 text-xs font-mono-data">
              <div className="flex justify-between"><span className="text-muted-foreground">{numConductors}× conductors</span><span>{fmt(conductorVol, 2)} in³</span></div>
              {clampVol > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Clamps (1× largest)</span><span>{fmt(clampVol, 2)} in³</span></div>}
              {deviceVol > 0 && <div className="flex justify-between"><span className="text-muted-foreground">{numDevices}× devices (2× each)</span><span>{fmt(deviceVol, 2)} in³</span></div>}
              {groundVol > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Grounds (1× largest)</span><span>{fmt(groundVol, 2)} in³</span></div>}
              <div className="flex justify-between font-bold border-t border-border pt-1 mt-1">
                <span>Total Required</span><span className="text-primary">{fmt(totalVol, 2)} in³</span>
              </div>
            </div>
          </div>

          {minBox ? (
            <div className="trade-card border-primary/40 bg-primary/5">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Minimum Box</span>
                <span className="font-mono-data text-lg text-primary font-bold">{minBox.label}</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Box volume: {minBox.volume} in³ — Required: {fmt(totalVol, 2)} in³ ({fmt((totalVol / minBox.volume) * 100, 1)}% used)
              </p>
            </div>
          ) : (
            <div className="trade-card border-destructive/40 bg-destructive/5">
              <p className="text-sm text-destructive font-semibold">⚠️ Exceeds standard box sizes</p>
              <p className="text-xs text-muted-foreground mt-1">Need {fmt(totalVol, 2)} in³ — consider a larger enclosure or reducing conductors.</p>
            </div>
          )}

          {minBox && onSave && (
            <button type="button" onClick={() => {
              onSave({
                calcType: "box-fill", label: `${minBox.label} — ${wireSize} AWG, ${numConductors} conductors`,
                inputs: { wireSize: `${wireSize} AWG`, conductors: String(numConductors), grounds: String(numGrounds), clamps: String(numClamps), devices: String(numDevices) },
                results: [
                  { label: "Min Box", value: minBox.label, unit: "" },
                  { label: "Box Volume", value: String(minBox.volume), unit: "in³" },
                  { label: "Required Volume", value: fmt(totalVol, 2), unit: "in³" },
                  { label: "Fill", value: fmt((totalVol / minBox.volume) * 100, 1), unit: "%" },
                ],
                note: "",
              });
              toast.success("Saved to Job Log");
            }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
          )}

          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-2">Standard Box Sizes</h3>
          <div className="-mx-4 overflow-x-auto">
            <div className="mx-4 rounded-lg border border-border">
              <table className="w-full text-left">
                <thead>
                  <tr>
                    <th className="data-table-header">Box Type</th>
                    <th className="data-table-header">Volume</th>
                    <th className="data-table-header">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {STANDARD_BOXES.map((b, i) => {
                    const fits = b.volume >= totalVol;
                    const isMin = b === minBox;
                    return (
                      <tr key={i} className={isMin ? "bg-primary/15 ring-1 ring-inset ring-primary/30" : i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                        <td className={`data-table-cell text-xs ${isMin ? "text-primary font-bold" : ""}`}>{b.label}</td>
                        <td className="data-table-cell font-mono-data">{b.volume} in³</td>
                        <td className={`data-table-cell font-semibold ${fits ? "text-primary" : "text-destructive"}`}>
                          {fits ? (isMin ? "✓ Min" : "✓") : "✗"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">
        ⚠️ Per NEC 314.16(B): Clamps count as 1× largest conductor regardless of quantity. All EGCs count as 1× largest conductor. Each device/yoke counts as 2× largest conductor.
      </p>
    </div>
  );
};

export const MultiRacewayFill = ({ onSave }: { onSave?: (entry: Parameters<ReturnType<typeof useJobLog>["addEntry"]>[0]) => void }) => {
  const [racewayIdx, setRacewayIdx] = useState(defaultRacewayIdx);
  const [wireEntries, setWireEntries] = useState<WireEntry[]>([
    { id: crypto.randomUUID(), size: "12", count: 3 },
  ]);

  const raceway = RACEWAY_TYPES[parseInt(racewayIdx)];

  const addEntry = () =>
    setWireEntries((prev) => [...prev, { id: crypto.randomUUID(), size: "12", count: 1 }]);

  const removeEntry = (id: string) =>
    setWireEntries((prev) => (prev.length > 1 ? prev.filter((e) => e.id !== id) : prev));

  const updateEntry = (id: string, field: keyof Omit<WireEntry, "id">, value: string) =>
    setWireEntries((prev) =>
      prev.map((e) =>
        e.id === id ? { ...e, [field]: field === "count" ? Math.max(0, parseInt(value, 10) || 0) : value } : e
      )
    );

  const validEntries = wireEntries.filter((e) => {
    const c = conductorAreas.find((ca) => ca.value === e.size);
    return c && e.count > 0;
  });

  const totalWires = validEntries.reduce((sum, e) => sum + e.count, 0);
  const totalArea = validEntries.reduce((sum, e) => {
    const c = conductorAreas.find((ca) => ca.value === e.size)!;
    return sum + c.area * e.count;
  }, 0);

  const fillLimit = getFillPercent(totalWires);
  const isValid = totalWires > 0;

  const minConduit = isValid
    ? raceway.sizes.find((c) => c.area * fillLimit >= totalArea)
    : null;

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        NEC Chapter 9 — Conduit fill for <span className="text-foreground font-semibold">EMT, IMC, RGS, PVC Sch 40 &amp; Sch 80</span>. Mixed THHN/THWN-2 conductors.
      </p>

      <div>
        <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Raceway Type</label>
        <Select value={racewayIdx} onValueChange={setRacewayIdx}>
          <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RACEWAY_TYPES.map((r, i) => (
              <SelectItem key={i} value={String(i)}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {wireEntries.map((entry, idx) => (
        <div key={entry.id} className="flex items-end gap-2">
          <div className="flex-1">
            {idx === 0 && (<label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Wire Size</label>)}
            <Select value={entry.size} onValueChange={(v) => updateEntry(entry.id, "size", v)}>
              <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
              <SelectContent>
                {conductorAreas.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-20">
            {idx === 0 && (<label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Qty</label>)}
            <Input type="number" inputMode="numeric" value={entry.count || ""} onChange={(e) => updateEntry(entry.id, "count", e.target.value)} placeholder="#" className="bg-secondary border-border font-mono-data text-lg" />
          </div>
          <button type="button" onClick={() => removeEntry(entry.id)} className="h-10 w-10 flex items-center justify-center rounded-md border border-border text-muted-foreground hover:text-destructive hover:border-destructive/50 transition-colors shrink-0" disabled={wireEntries.length === 1}>✕</button>
        </div>
      ))}

      <button type="button" onClick={addEntry} className="w-full h-9 rounded-md border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors">+ Add Wire Size</button>

      {isValid && (
        <div className="space-y-2 animate-fade-in">
          <div className="trade-card border-muted/40">
            <span className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">Conductor Breakdown</span>
            {validEntries.map((e) => {
              const c = conductorAreas.find((ca) => ca.value === e.size)!;
              return (
                <div key={e.id} className="flex justify-between text-xs font-mono-data">
                  <span className="text-muted-foreground">{e.count}× {c.label}</span>
                  <span className="text-foreground">{fmt(c.area * e.count, 4)} in²</span>
                </div>
              );
            })}
            <div className="flex justify-between text-sm font-mono-data font-bold border-t border-border mt-1 pt-1">
              <span className="text-muted-foreground">{totalWires} total</span>
              <span className="text-foreground">{fmt(totalArea, 4)} in²</span>
            </div>
          </div>

          <div className="trade-card border-accent/40 bg-accent/5">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Fill Limit ({raceway.label})</span>
              <span className="font-mono-data text-sm font-bold text-foreground">
                {totalWires === 1 ? "53%" : totalWires === 2 ? "31%" : "40%"}
                <span className="text-[10px] text-muted-foreground font-normal ml-1">({totalWires} {totalWires === 1 ? "wire" : "wires"})</span>
              </span>
            </div>
          </div>

          {minConduit ? (
            <div className="trade-card border-primary/40 bg-primary/5">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Minimum {raceway.label}</span>
                <span className="font-mono-data text-xl text-primary font-bold">{minConduit.label}</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Allowable: {fmt(minConduit.area * fillLimit, 4)} in² — Used: {fmt(totalArea, 4)} in² ({fmt((totalArea / minConduit.area) * 100, 1)}%)
              </p>
            </div>
          ) : (
            <div className="trade-card border-destructive/40 bg-destructive/5">
              <p className="text-sm text-destructive font-semibold">⚠️ Exceeds 4" {raceway.label} capacity</p>
            </div>
          )}

          {minConduit && onSave && (
            <button type="button" onClick={() => {
              const wireDesc = validEntries.map((e) => { const c = conductorAreas.find((ca) => ca.value === e.size)!; return `${e.count}× ${c.label}`; }).join(", ");
              onSave({
                calcType: "conduit-fill", label: `${minConduit.label} ${raceway.label} — ${wireDesc}`,
                inputs: Object.fromEntries(validEntries.map((e, i) => [`wire_${i + 1}`, `${e.count}× ${conductorAreas.find((ca) => ca.value === e.size)!.label}`])),
                results: [
                  { label: "Min Conduit", value: minConduit.label, unit: raceway.label },
                  { label: "Total Area", value: fmt(totalArea, 4), unit: "in²" },
                  { label: "Fill", value: fmt((totalArea / minConduit.area) * 100, 1), unit: "%" },
                ],
                note: "",
              });
              toast.success("Saved to Job Log");
            }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
          )}

          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-2">All {raceway.label} Sizes</h3>
          <div className="-mx-4 overflow-x-auto">
            <div className="mx-4 rounded-lg border border-border">
              <table className="w-full text-left">
                <thead><tr><th className="data-table-header">Size</th><th className="data-table-header">Area</th><th className="data-table-header">Fill Limit</th><th className="data-table-header">Status</th></tr></thead>
                <tbody>
                  {raceway.sizes.map((c, i) => {
                    const allowable = c.area * fillLimit;
                    const fits = allowable >= totalArea;
                    const isMin = c === minConduit;
                    return (
                      <tr key={i} className={isMin ? "bg-primary/15 ring-1 ring-inset ring-primary/30" : i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                        <td className={`data-table-cell ${isMin ? "text-primary font-bold" : ""}`}>{c.label}</td>
                        <td className="data-table-cell font-mono-data">{c.area}</td>
                        <td className="data-table-cell font-mono-data">{fmt(allowable, 4)}</td>
                        <td className={`data-table-cell font-semibold ${fits ? "text-primary" : "text-destructive"}`}>{fits ? (isMin ? "✓ Min" : "✓") : "✗"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">
        ⚠️ Based on NEC Chapter 9 Tables 4 &amp; 5 for THHN/THWN-2. Fill limits: 1 wire 53%, 2 wires 31%, 3+ wires 40%.
      </p>
    </div>
  );
};

export const ConduitBendingTool = ({ onSave }: { onSave?: (entry: Parameters<ReturnType<typeof useJobLog>["addEntry"]>[0]) => void }) => {
  const [calcType, setCalcType] = useState<"offset" | "stub90" | "3point" | "4point">("stub90");
  const [benderSize, setBenderSize] = useState('1/2"');
  const [stubHeight, setStubHeight] = useState("");
  const [offsetDepth, setOffsetDepth] = useState("");
  const [offsetAngle, setOffsetAngle] = useState("30");
  const [saddle3Depth, setSaddle3Depth] = useState("");
  const [saddle4Depth, setSaddle4Depth] = useState("");
  const [saddle4Width, setSaddle4Width] = useState("");

  const takeUp = TAKEUP_90[benderSize] ?? 5;
  const shoe = BENDER_SHOES.find((s) => s.size.startsWith(benderSize));
  const radius = shoe?.radius ?? 4;

  const stubH = parseFloat(stubHeight) || 0;
  const stubMark = stubH > 0 ? stubH - takeUp : 0;

  const offD = parseFloat(offsetDepth) || 0;
  const offAngle = parseInt(offsetAngle);
  const bendData = BEND_MATH.find((b) => b.angle === offAngle);
  const offMultiplier = bendData?.multiplier ?? 2.0;
  const offShrink = bendData?.shrinkPerInch ?? 0.25;
  const offDistance = offD > 0 ? offD * offMultiplier : 0;
  const offShrinkTotal = offD * offShrink;

  const sadD = parseFloat(saddle3Depth) || 0;
  const sadCenter = 45;
  const sadOuter = 22.5;
  const sadCenterMultiplier = 2.6;
  const sadSpacing = sadD * sadCenterMultiplier;
  const sadShrink = sadD * (3 / 8);

  const sad4D = parseFloat(saddle4Depth) || 0;
  const sad4W = parseFloat(saddle4Width) || 0;
  const sad4Angle = 22.5;
  const sad4Multiplier = 2.6;
  const sad4ShrinkPerInch = 3 / 16;
  const sad4Travel = sad4D * sad4Multiplier;
  const sad4ShrinkTotal = sad4D * sad4ShrinkPerInch * 2;
  const sad4CenterToInner = sad4W / 2;
  const sad4CenterToOuter = sad4CenterToInner + sad4Travel;

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Conduit bending reference with <span className="text-foreground font-semibold">minimum bend radii</span> per NEC, bending calculations for stubs, offsets, and saddles.
      </p>

      <div className="grid grid-cols-4 gap-2">
        {[
          { value: "stub90" as const, label: "90° Stub" },
          { value: "offset" as const, label: "Offset" },
          { value: "3point" as const, label: "3-Pt Saddle" },
          { value: "4point" as const, label: "4-Pt Saddle" },
        ].map((t) => (
          <button key={t.value} type="button" onClick={() => setCalcType(t.value)}
            className={`h-9 rounded-md text-xs font-semibold transition-colors border ${
              calcType === t.value ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/50"
            }`}>{t.label}</button>
        ))}
      </div>

      {calcType === "stub90" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Bender Size</label>
              <Select value={benderSize} onValueChange={setBenderSize}>
                <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(TAKEUP_90).map((s) => (
                    <SelectItem key={s} value={s}>{s} (Take-up: {TAKEUP_90[s]}")</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Stub Height (in)</label>
              <Input type="number" inputMode="decimal" value={stubHeight} onChange={(e) => setStubHeight(e.target.value)} placeholder="e.g. 12" className="bg-secondary border-border font-mono-data text-lg" />
            </div>
          </div>

          {stubH > 0 && (
            <div className="space-y-2 animate-fade-in">
              <div className="trade-card border-primary/40 bg-primary/5">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Mark from End</span>
                  <span className="font-mono-data text-xl text-primary font-bold">{fmt(stubMark, 1)}"</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">{stubH}" stub − {takeUp}" take-up = {fmt(stubMark, 1)}" mark</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-3 flex justify-center">
                <svg viewBox="0 0 200 160" className="w-48 h-32">
                  <line x1="20" y1="130" x2="120" y2="130" className="stroke-muted-foreground" strokeWidth="3" strokeLinecap="round" />
                  <path d={`M 120,130 A ${radius * 3},${radius * 3} 0 0 0 ${120 + radius * 3},${130 - radius * 3}`} fill="none" className="stroke-primary" strokeWidth="3" strokeLinecap="round" />
                  <line x1={120 + radius * 3} y1={130 - radius * 3} x2={120 + radius * 3} y2="20" className="stroke-muted-foreground" strokeWidth="3" strokeLinecap="round" />
                  <line x1={120 + radius * 3 + 12} y1="20" x2={120 + radius * 3 + 12} y2="130" className="stroke-primary/50" strokeWidth="1" strokeDasharray="3 2" />
                  <text x={120 + radius * 3 + 16} y="80" className="fill-primary text-[9px]" fontFamily="monospace">{stubH}"</text>
                  <line x1="20" y1="140" x2={120 - (stubMark > 0 ? stubMark * 0.8 : 0)} y2="140" className="stroke-safety" strokeWidth="1.5" />
                  <text x="30" y="152" className="fill-safety text-[8px]" fontFamily="monospace">Mark: {fmt(stubMark, 1)}"</text>
                </svg>
              </div>
            </div>
          )}

          {stubH > 0 && onSave && (
            <button type="button" onClick={() => {
              onSave({
                calcType: "conduit-bending", label: `90° Stub ${stubH}" — ${benderSize} bender`,
                inputs: { type: "90° Stub", bender: benderSize, stubHeight: `${stubH}"` },
                results: [
                  { label: "Mark from End", value: fmt(stubMark, 1), unit: "in" },
                  { label: "Take-up", value: String(takeUp), unit: "in" },
                ],
                note: "",
              });
              toast.success("Saved to Job Log");
            }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
          )}
        </div>
      )}

      {calcType === "offset" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Offset Depth (in)</label>
              <Input type="number" inputMode="decimal" value={offsetDepth} onChange={(e) => setOffsetDepth(e.target.value)} placeholder="e.g. 4" className="bg-secondary border-border font-mono-data text-lg" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Bend Angle</label>
              <Select value={offsetAngle} onValueChange={setOffsetAngle}>
                <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BEND_MATH.filter((b) => b.angle < 90).map((b) => (
                    <SelectItem key={b.angle} value={String(b.angle)}>{b.angle}° (×{b.multiplier})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {offD > 0 && (
            <div className="space-y-2 animate-fade-in">
              <div className="trade-card border-primary/40 bg-primary/5">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Distance Between Bends</span>
                  <span className="font-mono-data text-xl text-primary font-bold">{fmt(offDistance, 2)}"</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">{offD}" × {offMultiplier} = {fmt(offDistance, 2)}"</p>
              </div>
              <div className="trade-card">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Shrink</span>
                  <span className="font-mono-data text-lg font-bold text-foreground">{fmt(offShrinkTotal, 2)}"</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">{offD}" × {fmt(offShrink, 3)}" per inch = {fmt(offShrinkTotal, 2)}" total shrink</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-3 flex justify-center">
                <svg viewBox="0 0 220 100" className="w-full max-w-[14rem] h-20">
                  <line x1="10" y1="70" x2="70" y2="70" className="stroke-muted-foreground" strokeWidth="3" strokeLinecap="round" />
                  <line x1="70" y1="70" x2="110" y2="30" className="stroke-primary" strokeWidth="3" strokeLinecap="round" />
                  <line x1="110" y1="30" x2="210" y2="30" className="stroke-muted-foreground" strokeWidth="3" strokeLinecap="round" />
                  <line x1="70" y1="75" x2="110" y2="75" className="stroke-safety/50" strokeWidth="1" strokeDasharray="3 2" />
                  <text x="72" y="90" className="fill-safety text-[8px]" fontFamily="monospace">{fmt(offDistance, 1)}"</text>
                  <line x1="60" y1="30" x2="60" y2="70" className="stroke-primary/50" strokeWidth="1" strokeDasharray="3 2" />
                  <text x="38" y="55" className="fill-primary text-[8px]" fontFamily="monospace">{offD}"</text>
                </svg>
              </div>
            </div>
          )}

          {offD > 0 && onSave && (
            <button type="button" onClick={() => {
              onSave({
                calcType: "conduit-bending", label: `${offAngle}° Offset ${offD}" depth`,
                inputs: { type: "Offset", depth: `${offD}"`, angle: `${offAngle}°` },
                results: [
                  { label: "Distance Between Bends", value: fmt(offDistance, 2), unit: "in" },
                  { label: "Shrink", value: fmt(offShrinkTotal, 2), unit: "in" },
                ],
                note: "",
              });
              toast.success("Saved to Job Log");
            }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
          )}
        </div>
      )}

      {calcType === "3point" && (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Saddle Depth (in)</label>
            <Input type="number" inputMode="decimal" value={saddle3Depth} onChange={(e) => setSaddle3Depth(e.target.value)} placeholder="e.g. 2" className="bg-secondary border-border font-mono-data text-lg" />
          </div>

          {sadD > 0 && (
            <div className="space-y-2 animate-fade-in">
              <div className="trade-card border-primary/40 bg-primary/5">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Center Bend</span>
                  <span className="font-mono-data text-xl text-primary font-bold">{sadCenter}°</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="trade-card">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Outer Bends</span>
                  <span className="font-mono-data text-lg font-bold text-foreground">{sadOuter}° each</span>
                </div>
                <div className="trade-card">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Outer Spacing</span>
                  <span className="font-mono-data text-lg font-bold text-foreground">{fmt(sadSpacing, 1)}"</span>
                </div>
              </div>
              <div className="trade-card">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Shrink</span>
                  <span className="font-mono-data text-lg font-bold text-foreground">{fmt(sadShrink, 2)}"</span>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Mark center of obstruction on conduit. Mark {fmt(sadSpacing, 1)}" each side for outer {sadOuter}° bends. Center bend at {sadCenter}° (opposite direction). Add {fmt(sadShrink, 2)}" shrink.
              </p>
            </div>
          )}

          {sadD > 0 && onSave && (
            <button type="button" onClick={() => {
              onSave({
                calcType: "conduit-bending", label: `3-Pt Saddle ${sadD}" depth`,
                inputs: { type: "3-Point Saddle", depth: `${sadD}"` },
                results: [
                  { label: "Center Bend", value: String(sadCenter), unit: "°" },
                  { label: "Outer Bends", value: String(sadOuter), unit: "° each" },
                  { label: "Outer Spacing", value: fmt(sadSpacing, 1), unit: "in" },
                  { label: "Shrink", value: fmt(sadShrink, 2), unit: "in" },
                ],
                note: "",
              });
              toast.success("Saved to Job Log");
            }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
          )}
        </div>
      )}

      {calcType === "4point" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Saddle Depth (in)</label>
              <Input type="number" inputMode="decimal" value={saddle4Depth} onChange={(e) => setSaddle4Depth(e.target.value)} placeholder="e.g. 3" className="bg-secondary border-border font-mono-data text-lg" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Obstruction Width (in)</label>
              <Input type="number" inputMode="decimal" value={saddle4Width} onChange={(e) => setSaddle4Width(e.target.value)} placeholder="e.g. 4" className="bg-secondary border-border font-mono-data text-lg" />
            </div>
          </div>

          {sad4D > 0 && sad4W > 0 && (
            <div className="space-y-2 animate-fade-in">
              <div className="grid grid-cols-2 gap-2">
                <div className="trade-card border-primary/40 bg-primary/5">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider block">All Bends</span>
                  <span className="font-mono-data text-lg font-bold text-primary">{sad4Angle}° each</span>
                </div>
                <div className="trade-card">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Center → Inner</span>
                  <span className="font-mono-data text-lg font-bold text-foreground">{fmt(sad4CenterToInner, 1)}"</span>
                </div>
                <div className="trade-card">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Center → Outer</span>
                  <span className="font-mono-data text-lg font-bold text-foreground">{fmt(sad4CenterToOuter, 1)}"</span>
                </div>
                <div className="trade-card">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Total Shrink</span>
                  <span className="font-mono-data text-lg font-bold text-foreground">{fmt(sad4ShrinkTotal, 2)}"</span>
                </div>
              </div>
              <div className="bg-card border border-border rounded-xl p-3 flex justify-center">
                <svg viewBox="0 0 280 110" className="w-full max-w-[17rem] h-24">
                  <line x1="5" y1="75" x2="45" y2="75" className="stroke-muted-foreground" strokeWidth="3" strokeLinecap="round" />
                  <line x1="45" y1="75" x2="80" y2="35" className="stroke-primary" strokeWidth="3" strokeLinecap="round" />
                  <line x1="80" y1="35" x2="200" y2="35" className="stroke-primary" strokeWidth="3" strokeLinecap="round" />
                  <line x1="200" y1="35" x2="235" y2="75" className="stroke-primary" strokeWidth="3" strokeLinecap="round" />
                  <line x1="235" y1="75" x2="275" y2="75" className="stroke-muted-foreground" strokeWidth="3" strokeLinecap="round" />
                  <circle cx="45" cy="75" r="2.5" className="fill-primary" />
                  <circle cx="80" cy="35" r="2.5" className="fill-primary" />
                  <circle cx="200" cy="35" r="2.5" className="fill-primary" />
                  <circle cx="235" cy="75" r="2.5" className="fill-primary" />
                  <text x="28" y="90" className="fill-primary text-[7px]" fontFamily="monospace">{sad4Angle}°</text>
                  <text x="65" y="27" className="fill-primary text-[7px]" fontFamily="monospace">{sad4Angle}°</text>
                  <text x="192" y="27" className="fill-primary text-[7px]" fontFamily="monospace">{sad4Angle}°</text>
                  <text x="228" y="90" className="fill-primary text-[7px]" fontFamily="monospace">{sad4Angle}°</text>
                  <line x1="140" y1="30" x2="140" y2="80" className="stroke-safety/40" strokeWidth="1" strokeDasharray="2 2" />
                  <text x="133" y="98" className="fill-safety text-[7px]" fontFamily="monospace">CL</text>
                  <line x1="15" y1="35" x2="15" y2="75" className="stroke-muted-foreground/50" strokeWidth="1" strokeDasharray="2 2" />
                  <text x="2" y="58" className="fill-muted-foreground text-[7px]" fontFamily="monospace">{sad4D}"</text>
                  <line x1="80" y1="45" x2="200" y2="45" className="stroke-safety/50" strokeWidth="1" strokeDasharray="2 2" />
                  <text x="128" y="53" className="fill-safety text-[7px]" fontFamily="monospace">{sad4W}"</text>
                </svg>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Mark conduit center at obstruction center. Measure {fmt(sad4CenterToInner, 1)}" each side for inner bends, {fmt(sad4CenterToOuter, 1)}" each side for outer bends. Bend outer bends first ({sad4Angle}°), then inner bends ({sad4Angle}°). Add {fmt(sad4ShrinkTotal, 2)}" shrink to first measurement.
              </p>
            </div>
          )}

          {sad4D > 0 && sad4W > 0 && onSave && (
            <button type="button" onClick={() => {
              onSave({
                calcType: "conduit-bending", label: `4-Pt Saddle ${sad4D}" depth × ${sad4W}" wide`,
                inputs: { type: "4-Point Saddle", depth: `${sad4D}"`, width: `${sad4W}"` },
                results: [
                  { label: "Bend Angle", value: String(sad4Angle), unit: "° each" },
                  { label: "Center to Inner", value: fmt(sad4CenterToInner, 1), unit: "in" },
                  { label: "Center to Outer", value: fmt(sad4CenterToOuter, 1), unit: "in" },
                  { label: "Total Shrink", value: fmt(sad4ShrinkTotal, 2), unit: "in" },
                ],
                note: "",
              });
              toast.success("Saved to Job Log");
            }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
          )}
        </div>
      )}

      <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-2">Minimum Bend Radius — NEC Chapter 9, Table 2</h3>
      <div className="-mx-4 overflow-x-auto">
        <div className="mx-4 rounded-lg border border-border">
          <table className="w-full text-left">
            <thead><tr><th className="data-table-header">Size</th><th className="data-table-header">EMT (in)</th><th className="data-table-header">IMC (in)</th><th className="data-table-header">Rigid (in)</th></tr></thead>
            <tbody>
              {BEND_RADII.map((r, i) => (
                <tr key={r.size} className={i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                  <td className="data-table-cell font-mono-data font-semibold">{r.size}</td>
                  <td className="data-table-cell font-mono-data">{r.emt}</td>
                  <td className="data-table-cell font-mono-data">{r.imc}</td>
                  <td className="data-table-cell font-mono-data">{r.rigid}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-2">90° Stub Take-Up (Hand Benders)</h3>
      <div className="grid grid-cols-4 gap-2">
        {Object.entries(TAKEUP_90).map(([size, tu]) => (
          <div key={size} className={`bg-card rounded-lg p-2 border text-center ${
            size === benderSize && calcType === "stub90" ? "border-primary/40 bg-primary/5" : "border-border"
          }`}>
            <div className="text-xs text-muted-foreground">{size}</div>
            <div className="font-mono-data font-bold text-primary mt-0.5">{tu}"</div>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">
        ⚠️ Bend radii per NEC Chapter 9 Table 2 for one-shot bends with conductors. Take-up values are typical for standard hand benders — verify with your specific bender. Max 360° total bends between pull points per NEC 358.26/342.26/344.26.
      </p>
    </div>
  );
};
